import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MusicalForm = ({ data, onChange, onPosterChange, posterPreview, errors }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const genreOptions = [
    { value: 'broadway', label: 'Broadway' },
    { value: 'off_broadway', label: 'Off-Broadway' },
    { value: 'community', label: 'Community Theatre' },
    { value: 'school', label: 'School Production' },
    { value: 'opera', label: 'Opera' },
    { value: 'operetta', label: 'Operetta' },
    { value: 'jukebox', label: 'Jukebox Musical' },
    { value: 'rock', label: 'Rock Musical' },
    { value: 'pop', label: 'Pop Musical' },
    { value: 'classical', label: 'Classical' }
  ];

  // Validation functions
  const validateTitle = (value) => {
    if (!value || !value?.trim()) {
      return 'Musical name is required';
    }
    if (value?.trim()?.length < 2) {
      return 'Musical name must be at least 2 characters';
    }
    return null;
  };

  const validateYear = (value) => {
    if (!value) return null; // Optional field
    const year = parseInt(value, 10);
    const currentYear = new Date()?.getFullYear();
    if (isNaN(year)) {
      return 'Please enter a valid year';
    }
    if (year < 1800 || year > currentYear + 10) {
      return `Year must be between 1800 and ${currentYear + 10}`;
    }
    return null;
  };

  const validateUrl = (value) => {
    if (!value || !value?.trim()) {
      return null; // Optional field
    }
    try {
      const url = new URL(value);
      if (!['http:', 'https:']?.includes(url?.protocol)) {
        return 'URL must start with http:// or https://';
      }
      return null;
    } catch {
      return 'Please enter a valid URL';
    }
  };

  const handleInputChange = (field, value) => {
    // Handle numeric fields - convert empty strings to null for integer fields
    if (field === 'year_premiered') {
      const processedValue = value?.trim() === '' ? null : value?.trim() ? parseInt(value?.trim(), 10) : null;
      onChange({
        [field]: processedValue
      });
      
      // Validate year
      let error = validateYear(value);
      setFieldErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      onChange({
        [field]: value
      });

      // Real-time validation for title
      if (field === 'title') {
        let error = validateTitle(value);
        setFieldErrors(prev => ({
          ...prev,
          [field]: error
        }));
      }
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Validate on blur
    let error = null;
    if (field === 'title') {
      error = validateTitle(data?.title);
    } else if (field === 'year_premiered') {
      error = validateYear(data?.year_premiered);
    }

    setFieldErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileSelect(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    const fileType = file?.type;
    const fileSize = file?.size;

    // Validate file type
    if (!fileType?.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, WebP)');
      return;
    }

    // Validate file size (10MB limit)
    if (fileSize > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      onPosterChange?.(file, e?.target?.result);
      setIsUploading(false);
      setUploadProgress(100);
    };
    
    // Simulate progress for better UX
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 90) {
        clearInterval(progressInterval);
        progress = 90;
      }
      setUploadProgress(progress);
    }, 100);

    reader?.readAsDataURL(file);
  };

  const handleFileInputChange = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFileSelect(e?.target?.files?.[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef?.current?.click();
  };

  const handleRemovePoster = () => {
    onPosterChange?.(null, '');
    setUploadProgress(0);
    setIsUploading(false);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-8">
      {/* Production Details Section */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Music" size={20} className="text-blue-200" />
          <h2 className="text-xl font-heading font-semibold text-white">Production Details</h2>
          <span className="text-xs bg-red-500 bg-opacity-20 text-red-200 px-2 py-1 rounded-full">
            Required: Musical Name
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Musical Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={data?.title || ''}
              onChange={(e) => handleInputChange('title', e?.target?.value)}
              onBlur={() => handleBlur('title')}
              placeholder="Enter musical title"
              required
              className={`w-full px-4 py-2 bg-white bg-opacity-20 border rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 ${
                touched?.title && fieldErrors?.title
                  ? 'border-red-400 focus:ring-red-400' :'border-white border-opacity-30 focus:ring-blue-400'
              }`}
            />
            {touched?.title && fieldErrors?.title && (
              <div className="flex items-center space-x-1 mt-1">
                <Icon name="AlertCircle" size={14} className="text-red-300" />
                <p className="text-red-300 text-sm">{fieldErrors?.title}</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Year of First Production
            </label>
            <input
              type="number"
              value={data?.year_premiered ?? ''}
              onChange={(e) => handleInputChange('year_premiered', e?.target?.value)}
              onBlur={() => handleBlur('year_premiered')}
              placeholder="YYYY"
              min="1800"
              max={new Date()?.getFullYear() + 10}
              className={`w-full px-4 py-2 bg-white bg-opacity-20 border rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 ${
                touched?.year_premiered && fieldErrors?.year_premiered
                  ? 'border-red-400 focus:ring-red-400' :'border-white border-opacity-30 focus:ring-blue-400'
              }`}
            />
            {touched?.year_premiered && fieldErrors?.year_premiered && (
              <div className="flex items-center space-x-1 mt-1">
                <Icon name="AlertCircle" size={14} className="text-red-300" />
                <p className="text-red-300 text-sm">{fieldErrors?.year_premiered}</p>
              </div>
            )}
          </div>

          <Input
            label="Music By"
            type="text"
            value={data?.composer || ''}
            onChange={(e) => handleInputChange('composer', e?.target?.value)}
            placeholder="Composer name"
            className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
          />

          <Input
            label="Lyrics By"
            type="text"
            value={data?.lyricist || ''}
            onChange={(e) => handleInputChange('lyricist', e?.target?.value)}
            placeholder="Lyricist name"
            className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
          />

          <Input
            label="Book By"
            type="text"
            value={data?.book_writer || ''}
            onChange={(e) => handleInputChange('book_writer', e?.target?.value)}
            placeholder="Book writer name"
            className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
          />

          <Select
            label="Genre"
            value={data?.genre || 'broadway'}
            onChange={(e) => handleInputChange('genre', e?.target?.value)}
            options={genreOptions}
            className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
          />
        </div>
      </div>
      {/* Publishing Information Section */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="BookOpen" size={20} className="text-blue-200" />
          <h2 className="text-xl font-heading font-semibold text-white">Publishing Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Published By"
            type="text"
            value={data?.published_by || ''}
            onChange={(e) => handleInputChange('published_by', e?.target?.value)}
            placeholder="Publishing company"
            className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
          />

          <Input
            label="Rights Group"
            type="text"
            value={data?.rights_group || ''}
            onChange={(e) => handleInputChange('rights_group', e?.target?.value)}
            placeholder="Rights management organization"
            className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
          />
        </div>
      </div>
      {/* Visual Assets Section */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="Image" size={20} className="text-blue-200" />
          <h2 className="text-xl font-heading font-semibold text-white">Visual Assets</h2>
        </div>

        <div className="space-y-4">
          <label className="block text-sm font-medium text-blue-100 mb-2">
            Poster Image (JPG/PNG)
          </label>

          {/* File Upload Area */}
          <div
            className={`
              relative border-2 border-dashed rounded-xl transition-all duration-200 p-8
              ${dragActive 
                ? 'border-blue-400 bg-blue-400 bg-opacity-10' :'border-white border-opacity-30 hover:border-white hover:border-opacity-50'
              }
              ${isUploading ? 'pointer-events-none opacity-70' : ''}
            `}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {posterPreview ? (
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <img
                    src={posterPreview}
                    alt="Poster preview"
                    className="max-h-48 max-w-full object-contain rounded-xl shadow-lg"
                  />
                  {!isUploading && (
                    <button
                      type="button"
                      onClick={handleRemovePoster}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      aria-label="Remove poster"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  )}
                  
                  {/* Upload progress overlay */}
                  {isUploading && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-xl">
                      <div className="text-white text-center">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-sm">Processing...</p>
                        <div className="w-32 h-2 bg-white bg-opacity-20 rounded-full mt-2">
                          <div 
                            className="h-full bg-white rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {!isUploading && (
                  <button
                    type="button"
                    onClick={handleBrowseClick}
                    className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-xl hover:bg-opacity-30 transition-colors"
                  >
                    Change Image
                  </button>
                )}
              </div>
            ) : (
              <div className="text-center">
                {isUploading ? (
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-2 border-blue-200 border-t-transparent rounded-full animate-spin mb-4"></div>
                    <p className="text-white mb-2">Processing image...</p>
                    <div className="w-48 h-2 bg-white bg-opacity-20 rounded-full">
                      <div 
                        className="h-full bg-blue-400 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <>
                    <Icon name="Upload" size={48} className="text-blue-200 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">
                      Drop poster image here
                    </h3>
                    <p className="text-blue-100 text-sm mb-4">
                      or click to browse for files
                    </p>
                    <button
                      type="button"
                      onClick={handleBrowseClick}
                      className="px-6 py-2 bg-white bg-opacity-20 text-white rounded-xl hover:bg-opacity-30 transition-colors"
                    >
                      Browse Files
                    </button>
                    <p className="text-xs text-blue-200 mt-2">
                      JPG, PNG, WebP up to 10MB
                    </p>
                  </>
                )}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              aria-label="Upload poster image"
              disabled={isUploading}
            />
          </div>
        </div>
      </div>
      {/* Description Section */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Icon name="FileText" size={20} className="text-blue-200" />
          <h2 className="text-xl font-heading font-semibold text-white">Description</h2>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-blue-100 mb-2">
            Musical Description (Optional)
          </label>
          <textarea
            id="description"
            value={data?.description || ''}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            placeholder="Brief description of the musical's plot, themes, or notable features..."
            rows={4}
            className="w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 text-white placeholder-blue-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-vertical"
          />
        </div>
      </div>
    </div>
  );
};

export default MusicalForm;